import { QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DismantlingBayType } from "../models/DismantlingBay";
import {
  IAllocateVehicleRequest,
  IPlannerVehicleRow,
  IPlannerVehicleRowResponse,
  IPlannerVehicleRowsResponse,
  IReorderBayRequest,
} from "../models/ProductionPlanner";
import { DefaultResponse } from "../models/system";

interface UseProductionPlannerArgs {
  from?: string;
  to?: string;
}

interface PlannerMutationContext {
  previousAwaiting: IPlannerVehicleRowsResponse | undefined;
  previousAllocated: [QueryKey, IPlannerVehicleRowsResponse | undefined][];
}

const findVehicleInQueries = (
  vehicleId: string,
  queries: [QueryKey, IPlannerVehicleRowsResponse | undefined][]
): IPlannerVehicleRow | undefined => {
  for (const [, value] of queries) {
    const match = value?.data?.find((v) => v.id === vehicleId);
    if (match) return match;
  }
  return undefined;
};

export default function useProductionPlanner({ from, to }: UseProductionPlannerArgs = {}) {
  const { get, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const awaiting = useQuery({
    queryKey: ["planner-awaiting"],
    queryFn: () => get<IPlannerVehicleRowsResponse>("/productionplanner/awaiting"),
    staleTime: 1000 * 30,
  });

  const allocated = useQuery({
    queryKey: ["planner-allocated", from, to],
    queryFn: () => get<IPlannerVehicleRowsResponse>(`/productionplanner?from=${from}&to=${to}`),
    enabled: !!from && !!to,
    staleTime: 1000 * 30,
  });

  const invalidatePlanner = () => {
    queryClient.invalidateQueries(["planner-awaiting"]);
    queryClient.invalidateQueries(["planner-allocated"]);
    queryClient.invalidateQueries(["production-manager-overview"]);
  };

  const snapshotPlanner = async (): Promise<PlannerMutationContext> => {
    await queryClient.cancelQueries(["planner-awaiting"]);
    await queryClient.cancelQueries(["planner-allocated"]);

    return {
      previousAwaiting: queryClient.getQueryData<IPlannerVehicleRowsResponse>([
        "planner-awaiting",
      ]),
      previousAllocated: queryClient.getQueriesData<IPlannerVehicleRowsResponse>([
        "planner-allocated",
      ]),
    };
  };

  const rollbackPlanner = (context: PlannerMutationContext | undefined) => {
    if (!context) return;
    if (context.previousAwaiting) {
      queryClient.setQueryData(["planner-awaiting"], context.previousAwaiting);
    }
    context.previousAllocated.forEach(([key, value]) => {
      queryClient.setQueryData(key, value);
    });
  };

  const allocateVehicle = useMutation<
    IPlannerVehicleRowResponse,
    unknown,
    { vehicleId: string, body: IAllocateVehicleRequest },
    PlannerMutationContext
  >(
    ({ vehicleId, body }) =>
      put<IAllocateVehicleRequest, IPlannerVehicleRowResponse>(
        `/productionplanner/${vehicleId}/allocate`,
        body
      ),
    {
      onMutate: async ({ vehicleId, body }) => {
        const context = await snapshotPlanner();

        const sourceVehicle =
          context.previousAwaiting?.data?.find((v) => v.id === vehicleId) ??
          findVehicleInQueries(vehicleId, context.previousAllocated);

        if (!sourceVehicle) return context;

        const updatedVehicle: IPlannerVehicleRow = {
          ...sourceVehicle,
          dismantlingBayId: body.dismantlingBayId,
          plannedDismantlingOn: body.plannedOn,
        };

        if (context.previousAwaiting) {
          queryClient.setQueryData<IPlannerVehicleRowsResponse>(["planner-awaiting"], {
            ...context.previousAwaiting,
            data: context.previousAwaiting.data.filter((v) => v.id !== vehicleId),
          });
        }

        queryClient.setQueriesData<IPlannerVehicleRowsResponse>(
          ["planner-allocated"],
          (current) => {
            if (!current) return current;
            const others = current.data.filter((v) => v.id !== vehicleId);
            return { ...current, data: [...others, updatedVehicle] };
          }
        );

        return context;
      },
      onError: (_err, _vars, context) => rollbackPlanner(context),
      onSettled: () => invalidatePlanner(),
    }
  );

  const clearAllocation = useMutation<
    DefaultResponse,
    unknown,
    string,
    PlannerMutationContext
  >((vehicleId) => del(`/productionplanner/${vehicleId}/allocation`), {
    onMutate: async (vehicleId) => {
      const context = await snapshotPlanner();

      const sourceVehicle = findVehicleInQueries(vehicleId, context.previousAllocated);

      queryClient.setQueriesData<IPlannerVehicleRowsResponse>(
        ["planner-allocated"],
        (current) => {
          if (!current) return current;
          return { ...current, data: current.data.filter((v) => v.id !== vehicleId) };
        }
      );

      if (sourceVehicle && context.previousAwaiting) {
        const cleared: IPlannerVehicleRow = {
          ...sourceVehicle,
          dismantlingBayId: null,
          plannedDismantlingOn: null,
        };
        const others = context.previousAwaiting.data.filter((v) => v.id !== vehicleId);
        queryClient.setQueryData<IPlannerVehicleRowsResponse>(["planner-awaiting"], {
          ...context.previousAwaiting,
          data: [cleared, ...others],
        });
      }

      return context;
    },
    onError: (_err, _vehicleId, context) => rollbackPlanner(context),
    onSettled: () => invalidatePlanner(),
  });

  const reorderBay = useMutation<
    DefaultResponse,
    unknown,
    IReorderBayRequest,
    PlannerMutationContext
  >((body) => put<IReorderBayRequest, DefaultResponse>("/productionplanner/reorder", body), {
    onMutate: async (body) => {
      const context = await snapshotPlanner();

      const orderById = new Map(body.vehicleIds.map((id, index) => [id, index]));

      queryClient.setQueriesData<IPlannerVehicleRowsResponse>(
        ["planner-allocated"],
        (current) => {
          if (!current) return current;
          return {
            ...current,
            data: current.data.map((v) =>
              orderById.has(v.id)
                ? { ...v, dismantlingBayOrder: orderById.get(v.id) as number }
                : v
            ),
          };
        }
      );

      return context;
    },
    onError: (_err, _vars, context) => rollbackPlanner(context),
    onSettled: () => invalidatePlanner(),
  });

  const clearDay = useMutation(
    ({ date, dismantlingBayType }: { date: string, dismantlingBayType: DismantlingBayType }) =>
      del<DefaultResponse>(
        `/productionplanner/day?date=${date}&dismantlingBayType=${dismantlingBayType}`
      ),
    {
      onSuccess: () => invalidatePlanner(),
    }
  );

  return {
    awaiting,
    allocated,
    allocateVehicle,
    clearAllocation,
    clearDay,
    reorderBay,
  };
}
